import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, BookOpen, BrainCircuit, HelpCircle, RotateCcw, Send } from 'lucide-react'
import SearchBar from '../components/SearchBar'
import MateriaCard from '../components/MateriaCard'
import VideoaulaCard from '../components/VideoaulaCard'
import FeatureTour from '../components/FeatureTour'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const frases = [
  'Cada passo te aproxima do seu objetivo! 🎯',
  'A persistência é o caminho do êxito. 💪',
  'Conhecimento é algo que ninguém pode tirar de você. 📚',
  'Hoje é um ótimo dia para aprender algo novo! ✨',
  'Pequenos progressos diários viram grandes conquistas. 🌟'
]

function HomePage() {
  const [tour, setTour] = useState({ open: false, initialStep: 0 })
  const usuario = useAuthStore(state => state.usuario)
  const accessToken = useAuthStore(state => state.accessToken)
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['materias'],
    queryFn: async () => (await api.get('/materias')).data.materias
  })
  const { data: recomendacoes = [] } = useQuery({
    queryKey: ['recomendacoes'],
    queryFn: async () => (await api.get('/recomendacoes')).data.recomendacoes,
    enabled: Boolean(accessToken)
  })

  const fraseMotivacional = frases[new Date().getDate() % frases.length]

  useEffect(() => {
    let shouldOpen = false
    try {
      shouldOpen = localStorage.getItem('eduplex-feature-tour-v1') !== 'completed'
    } catch (_) {
      shouldOpen = false
    }

    if (!shouldOpen) return undefined
    const timer = window.setTimeout(() => setTour({ open: true, initialStep: 0 }), 700)
    return () => window.clearTimeout(timer)
  }, [])

  const openTour = initialStep => setTour({ open: true, initialStep })
  const closeTour = () => {
    try {
      localStorage.setItem('eduplex-feature-tour-v1', 'completed')
    } catch (_) {
      // O tour continua funcionando mesmo quando o navegador bloqueia o armazenamento.
    }
    setTour(current => ({ ...current, open: false }))
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h1 className="mb-1 text-xl font-medium lg:text-2xl">
          Olá, {usuario?.nome?.split(' ')[0] || 'estudante'}! 👋
        </h1>
        <p className="text-sm text-gray-500">{fraseMotivacional}</p>
      </div>

      <SearchBar />

      {usuario?.tipo_perfil === 'professor' && (
        <section className="flex flex-col gap-4 rounded-2xl border border-violet-100 bg-violet-50/60 p-4 dark:border-violet-950 dark:bg-violet-950/30 sm:flex-row sm:items-center sm:justify-between lg:p-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-violet-700 shadow-sm dark:bg-zinc-900 dark:text-violet-300">
              <Send size={18} />
            </span>
            <div>
              <h2 className="text-sm font-medium">Área do professor</h2>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-zinc-400">Encontrou uma aula importante? Envie o link aos alunos da sua turma.</p>
            </div>
          </div>
          <Link to="/recomendar" className="btn-primary inline-flex shrink-0 items-center justify-center gap-1.5 px-4 py-2 text-xs">
            Recomendar aula <ArrowRight size={14} />
          </Link>
        </section>
      )}

      {recomendacoes.length > 0 && (
        <section>
          <h2 className="text-sm font-medium mb-1">Recomendadas por professores</h2>
          <p className="text-xs text-gray-400 mb-3">Links enviados pela sua instituição ou turma para apoiar seus estudos.</p>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {recomendacoes.slice(0, 3).map(item => (
              <VideoaulaCard key={item.id} contextLabel={item.turma?.nome || item.instituicao?.nome} videoaula={{
                ...item,
                fonte: 'recomendacao',
                professor: item.professor?.nome || 'Professor',
                canal: item.canal || 'Recomendação',
                duracao_segundos: 0,
                avaliacao_media: 0
              }} />
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-sm font-medium">Matérias</h2>
            <p className="text-xs text-gray-400 mt-0.5">Escolha por onde quer começar.</p>
          </div>
        </div>

        {isLoading && <p className="text-sm text-gray-500">Carregando matérias...</p>}

        {isError && (
          <div className="card text-sm">
            <p className="text-red-600">{getApiError(error, 'Não foi possível carregar as matérias.')}</p>
            <button type="button" className="btn-secondary mt-3" onClick={() => refetch()}>
              Tentar novamente
            </button>
          </div>
        )}

        {data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {data.map(materia => <MateriaCard key={materia.id} materia={materia} />)}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 dark:border-zinc-800 dark:bg-zinc-900/50 lg:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium lg:text-lg">Comece por aqui</h2>
            <p className="mt-1 text-sm text-gray-500">Conheça as ferramentas que ajudam a organizar e lembrar o que você estudou.</p>
          </div>
          <button
            type="button"
            onClick={() => openTour(0)}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium text-gray-500 hover:bg-white hover:text-black dark:hover:bg-zinc-800 dark:hover:text-white"
          >
            <HelpCircle size={15} /> <span className="hidden sm:inline">Fazer tour</span>
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <article className="rounded-2xl border border-cyan-100 bg-white p-4 dark:border-cyan-950 dark:bg-zinc-950 lg:p-5">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-300">
              <BrainCircuit size={19} />
            </div>
            <h3 className="font-medium">Tutor IA</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">Envie uma dúvida ou foto e aprenda com uma explicação passo a passo.</p>
            <Link to={usuario ? '/tutor' : '/login'} className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-cyan-600 hover:underline">
              Abrir Tutor IA <ArrowRight size={14} />
            </Link>
          </article>

          <article className="rounded-2xl border border-blue-100 bg-white p-4 dark:border-blue-950 dark:bg-zinc-950 lg:p-5">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
              <BookOpen size={19} />
            </div>
            <h3 className="font-medium">Caderno digital</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">Guarde anotações, tags, favoritos e PDFs na sua conta.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to="/caderno" className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
                Abrir Caderno <ArrowRight size={14} />
              </Link>
              <button type="button" onClick={() => openTour(0)} className="text-xs font-medium text-gray-500 hover:text-black dark:hover:text-white">
                Como funciona
              </button>
            </div>
          </article>

          <article className="rounded-2xl border border-violet-100 bg-white p-4 dark:border-violet-950 dark:bg-zinc-950 lg:p-5">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-300">
              <RotateCcw size={19} />
            </div>
            <h3 className="font-medium">Revisão espaçada</h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">Revise cada tema no momento certo para lembrar por mais tempo.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to="/revisao" className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:underline">
                Abrir Revisão <ArrowRight size={14} />
              </Link>
              <button type="button" onClick={() => openTour(1)} className="text-xs font-medium text-gray-500 hover:text-black dark:hover:text-white">
                Como funciona
              </button>
            </div>
          </article>
        </div>
      </section>

      <FeatureTour open={tour.open} initialStep={tour.initialStep} onClose={closeTour} />
    </div>
  )
}

export default HomePage
